import axios, { AxiosError, type AxiosResponse } from "axios";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { endpoints } from "@/endpoints";
import useApiStore from "@/stores/api";
import type {
  VisitsRequest,
  UpdateVisitorParams,
  UpdateVisitParams,
  VisitResponse,
  UpdateVisitorContext,
  UpdateVisitContext,
  ExportPayload,
  SignOutAllVisitsParams,
  SignOutSelectedVisitsParams,
} from "./types";
import { cloneDeep } from "lodash";
import { STATUS_ID, STATUS } from "@/stores/constants";
import dayjs from "dayjs";
import useVisitStore from "@/stores/visit";
import { asyncTimeout } from "@/utils/global";
import { useI18n } from "vue-i18n";
import { useLocationSelector } from "@/composables/location";
import download from "downloadjs";
import { visitKeys } from ".";
import {
  ReviewStatus,
  type ListVisitItem,
  type GetVisitResponseApiResponse,
  type ListVisitItemPagedApiResponse,
  SourceTypes,
  type CountVisitResponseApiResponse,
} from "../zoolander";

const LOADING_TIMEOUT = 1000;

export const useCreateVisitor = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const apiStore = useApiStore();

  return useMutation(
    (payload: VisitsRequest) => {
      return axios.post(
        apiStore.getEndpoint("platform", endpoints.visitors.create),
        payload
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(visitKeys._def);
        onSuccess?.();
      },
    }
  );
};

export const useUpdateVisitor = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { getEndpoint } = useApiStore();
  const { setVisitLoading } = useVisitStore();

  return useMutation<
    AxiosResponse,
    AxiosError,
    UpdateVisitorParams,
    UpdateVisitorContext
  >(
    ({ pk, payload }) =>
      axios.put(
        getEndpoint("platform", endpoints.visitors.update(pk)),
        payload
      ),
    {
      onMutate: async ({ pk, payload, filters }) => {
        if (!filters) return;

        setVisitLoading(pk, true);

        await asyncTimeout(LOADING_TIMEOUT);

        const updateVisit = (visit: ListVisitItem) => {
          const now = new Date();
          switch (payload.currentStatus) {
            case STATUS_ID.IN:
              visit.startedAt = now;
              visit.finishedAt = undefined;
              break;
            case STATUS_ID.OUT:
              visit.finishedAt = now;
              visit.sourceOut = SourceTypes.Dashboard;
              break;
          }
        };

        const listQueryKey = visitKeys.list(filters).queryKey;
        queryClient.cancelQueries({ queryKey: listQueryKey });
        const previousListVisit =
          queryClient.getQueryData<ListVisitItemPagedApiResponse>(listQueryKey);
        if (!previousListVisit) return;
        const newListVisit = cloneDeep(previousListVisit);
        const updatingVisit = newListVisit.data.find(
          (visit) => visit.id === pk
        );
        if (!updatingVisit) return;
        updateVisit(updatingVisit);
        queryClient.setQueryData(listQueryKey, newListVisit);

        const byIdQueryKey = visitKeys.byId(pk).queryKey;
        queryClient.cancelQueries({ queryKey: byIdQueryKey });
        const previousByIdVisit =
          queryClient.getQueryData<GetVisitResponseApiResponse>(byIdQueryKey);
        if (previousByIdVisit) {
          const newByIdVisit = cloneDeep(previousByIdVisit);
          updateVisit(newByIdVisit.data);
          queryClient.setQueryData(byIdQueryKey, newByIdVisit);
        }

        const countQueryKey = visitKeys.count(filters).queryKey;
        const previousCounts =
          queryClient.getQueryData<CountVisitResponseApiResponse>(
            countQueryKey
          );
        if (previousCounts) {
          const newCounts = cloneDeep(previousCounts);
          if (payload.currentStatus === STATUS_ID.IN) {
            newCounts.data.in += 1;
            newCounts.data.total += 1;
          }
          if (payload.currentStatus === STATUS_ID.OUT) {
            newCounts.data.in -= 1;
          }
          queryClient.setQueryData(countQueryKey, newCounts);
        }

        setVisitLoading(pk, false);

        return {
          listQueryKey,
          previousListVisit,
          byIdQueryKey,
          previousByIdVisit,
          countQueryKey,
          previousCounts,
        };
      },
      onSuccess: (_data, { payload }) => {
        if (payload.currentStatus) return;

        queryClient.invalidateQueries(visitKeys._def);
        onSuccess?.();
      },
      onError: (_error, _vars, context) => {
        if (!context) return;
        const {
          listQueryKey,
          previousListVisit,
          byIdQueryKey,
          previousByIdVisit,
          countQueryKey,
          previousCounts,
        } = context;
        queryClient.setQueryData(listQueryKey, previousListVisit);
        queryClient.setQueryData(byIdQueryKey, previousByIdVisit);
        queryClient.setQueryData(countQueryKey, previousCounts);
      },
    }
  );
};

export const useUpdateVisit = () => {
  const queryClient = useQueryClient();
  const { getEndpoint } = useApiStore();
  const { setVisitLoading } = useVisitStore();

  return useMutation<
    AxiosResponse<VisitResponse>,
    AxiosError,
    UpdateVisitParams,
    UpdateVisitContext
  >(
    ({ pk, payload }) =>
      axios.patch(
        getEndpoint("platform", endpoints.visits.update(pk)),
        payload
      ),
    {
      onMutate: async ({ pk, payload, filters }) => {
        if (!filters) return;

        setVisitLoading(pk, true);

        await asyncTimeout(LOADING_TIMEOUT);

        const updateVisit = (visit: ListVisitItem) => {
          if (payload.review_status === STATUS.ALLOW) {
            visit.reviewStatus = ReviewStatus.Allow;
          }

          if (payload.review_status === STATUS.DENY) {
            visit.reviewStatus = ReviewStatus.Deny;
            visit.finishedAt = new Date();
          }
        };

        const listQueryKey = visitKeys.list(filters).queryKey;
        queryClient.cancelQueries({ queryKey: listQueryKey });
        const previousListVisit =
          queryClient.getQueryData<ListVisitItemPagedApiResponse>(listQueryKey);
        if (!previousListVisit) return;
        const newListVisit = cloneDeep(previousListVisit);
        const updatingVisit = newListVisit.data.find(
          (visit) => visit.id === pk
        );
        if (!updatingVisit) return;
        updateVisit(updatingVisit);
        queryClient.setQueryData(listQueryKey, newListVisit);

        const byIdQueryKey = visitKeys.byId(pk).queryKey;

        queryClient.cancelQueries({ queryKey: byIdQueryKey });
        const previousByIdVisit =
          queryClient.getQueryData<GetVisitResponseApiResponse>(byIdQueryKey);
        if (previousByIdVisit) {
          const newByIdVisit = cloneDeep(previousByIdVisit);
          updateVisit(newByIdVisit.data);
          queryClient.setQueryData(byIdQueryKey, newByIdVisit);
        }

        setVisitLoading(pk, false);

        return {
          listQueryKey,
          previousListVisit,
          byIdQueryKey,
          previousByIdVisit,
        };
      },
      onError: (_error, _vars, context) => {
        if (!context) return;
        const {
          listQueryKey,
          previousListVisit,
          byIdQueryKey,
          previousByIdVisit,
        } = context;
        queryClient.setQueryData(listQueryKey, previousListVisit);
        queryClient.setQueryData(byIdQueryKey, previousByIdVisit);
      },
      onSettled() {
        queryClient.invalidateQueries(visitKeys._def);
      },
    }
  );
};

export const useExportVisits = () => {
  const apiStore = useApiStore();
  const { t } = useI18n();
  const { locationParams } = useLocationSelector();
  const FORMAT_CSV_NAME = "YYYY-MM-DD HH.mm.ss";

  return useMutation<void, AxiosError, ExportPayload>(async (payload) => {
    const { data } = await axios.get<Blob>(
      apiStore.getEndpoint("platform", endpoints.visits.csv),
      {
        params: {
          ...payload,
          ...locationParams.value,
        },
        responseType: "blob",
      }
    );

    const fileName = t("timelines.visitsCsvFileName", {
      datetime: dayjs().format(FORMAT_CSV_NAME),
    });

    download(data, `${fileName}.csv`, "application/csv");
  });
};

export const useSignOutAllVisits = () => {
  const apiStore = useApiStore();
  const { locationParams } = useLocationSelector();
  const queryClient = useQueryClient();
  const visitStore = useVisitStore();

  return useMutation<AxiosResponse, AxiosError, SignOutAllVisitsParams>(
    ({ payload }) =>
      axios.post(
        apiStore.getEndpoint("platform", endpoints.visitors.signOutAll),
        {
          ...locationParams.value,
          ...payload,
        }
      ),
    {
      onMutate: ({ queryKey }) => {
        const previousListVisit =
          queryClient.getQueryData<ListVisitItemPagedApiResponse>(queryKey);

        if (!previousListVisit) return;

        previousListVisit.data.forEach((visit) => {
          if (visit?.id && visit?.startedAt && !visit.finishedAt) {
            visitStore.setVisitLoading(visit.id, true);
          }
        });
      },
      onSuccess: (_data, { queryKey, countQueryKey }) => {
        const previousListVisit =
          queryClient.getQueryData<ListVisitItemPagedApiResponse>(queryKey);

        if (!previousListVisit) return;

        const newListVisit = cloneDeep(previousListVisit);

        const now = new Date();

        newListVisit.data.forEach((visit) => {
          if (visit?.id && visit?.startedAt && !visit.finishedAt) {
            visit.finishedAt = now;
            visitStore.setVisitLoading(visit.id, false);
          }
        });

        queryClient.setQueryData(queryKey, newListVisit);

        const previousCounts =
          queryClient.getQueryData<CountVisitResponseApiResponse>(
            countQueryKey
          );
        if (previousCounts) {
          const newCounts = cloneDeep(previousCounts);
          newCounts.data.in = 0;
          queryClient.setQueryData(countQueryKey, newCounts);
        }
      },
      onError(_error, { queryKey }) {
        const previousListVisit =
          queryClient.getQueryData<ListVisitItemPagedApiResponse>(queryKey);

        if (!previousListVisit) return;

        previousListVisit.data.forEach((visit) => {
          if (visit?.id && visit?.startedAt && !visit.finishedAt) {
            visitStore.setVisitLoading(visit.id, false);
          }
        });
      },
      onSettled: (_data, _error) => {
        queryClient.invalidateQueries(visitKeys._def);
      },
    }
  );
};

export const useSignOutSelectedVisits = () => {
  const apiStore = useApiStore();
  const queryClient = useQueryClient();
  const visitStore = useVisitStore();

  return useMutation<
    AxiosResponse,
    AxiosError,
    SignOutSelectedVisitsParams,
    {
      previousVisits?: ListVisitItemPagedApiResponse;
      previousCounts?: CountVisitResponseApiResponse;
    }
  >(
    ({ payload }) =>
      axios.put(
        apiStore.getEndpoint("platform", endpoints.visits.signOutSelected),
        {
          visit_id: payload,
        }
      ),
    {
      async onMutate({ queryKey, payload: visitIds, countQueryKey }) {
        queryClient.cancelQueries({
          queryKey,
        });

        const previousVisits =
          queryClient.getQueryData<ListVisitItemPagedApiResponse>(queryKey);

        if (!previousVisits) return;

        previousVisits.data.forEach((visit) => {
          if (visit?.id && visitIds.includes(visit?.id)) {
            visitStore.setVisitLoading(visit.id, true);
          }
        });

        await asyncTimeout(LOADING_TIMEOUT);

        const newVisits = cloneDeep(previousVisits);
        const now = new Date();

        const previousCounts =
          queryClient.getQueryData<CountVisitResponseApiResponse>(
            countQueryKey
          );

        const newCounts = cloneDeep(previousCounts);

        newVisits.data.forEach((visit) => {
          if (visit?.id && visitIds.includes(visit?.id)) {
            visitStore.setVisitLoading(visit.id, false);
            visit.finishedAt = now;
            if (newCounts) {
              newCounts.data.in -= 1;
            }
          }
        });

        if (newCounts) {
          queryClient.setQueryData(countQueryKey, newCounts);
        }

        queryClient.setQueryData(queryKey, newVisits);

        return { previousVisits, previousCounts };
      },
      onError(_error, { queryKey, countQueryKey }, context) {
        if (!context) return;
        const { previousVisits, previousCounts } = context;
        queryClient.setQueryData(queryKey, previousVisits);
        queryClient.setQueryData(countQueryKey, previousCounts);
      },
      onSettled() {
        queryClient.invalidateQueries(visitKeys._def);
      },
    }
  );
};

export const useAnonymizeVisits = () => {
  const apiStore = useApiStore();
  const queryClient = useQueryClient();
  const { locationParams } = useLocationSelector();

  return useMutation<AxiosResponse, AxiosError, string[]>(
    (visitIds) =>
      axios.put(apiStore.getEndpoint("platform", endpoints.visits.anonymize), {
        ...locationParams.value,
        visit_id: visitIds,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries(visitKeys._def);
      },
    }
  );
};
